﻿import { MultiSelectDropdown as msdropdown } from "../modules/multiselectdropdown";
import SuppressesEvent from "./suppressesevent";
import AsyncFileField from "../modules/asyncfilefield"
import DateRangeField, { IDateRangeElement } from "../modules/daterangefield";
import { DualListBox as duallistbox } from "../modules/duallistbox";

declare global {
  interface JQuery {
    autoNumeric(option, args): any;
  }
}

export class Element {
  private _$element: JQuery;
  public get $element(): JQuery { return this._$element; }
  public get element(): HTMLElement { return this.$element[0]; }

  public get $userFacingElement(): JQuery { return this.type.$userFacingElement; }
  public get userFacingElement(): HTMLElement { return this.type.$userFacingElement[0]; }
  public get $valueElement(): JQuery { return this.type.$valueElement; }
  public get valueElement(): HTMLElement { return this.type.$valueElement[0]; }

  private type: IElement;

  public get $(): JQuery { return this._$element; }

  public get isInput(): boolean {
    return this.type instanceof Input;
  }

  public get isSelect(): boolean {
    return this.type instanceof Select;
  }

  public get isDisplay(): boolean {
    return this.type instanceof DisplayField;
  }

  public get isRadio(): boolean {
    return this.type instanceof RadioGroup;
  }

  public get isCheckbox(): boolean {
    return this.type instanceof Checkbox;
  }

  public get isFile(): boolean {
    return this.type instanceof File;
  }

  public get isMultiSelectDropdown(): boolean {
    return this.type instanceof MultiSelectDropdown;
  }

  public get isDateField(): boolean {
    return this.type instanceof DateField;
  }

  public get isDateRangeDateField(): boolean {
    return this.type instanceof DateRangeDateField;
  }

  public get isDateRange(): boolean {
    return this.type instanceof DateRange;
  }

  public get isMoneyField(): boolean {
    return this.type instanceof MoneyField;
  }

  public get isGeneric(): boolean {
    return this.type instanceof GenericElement;
  }

  private static map: any[] = [
    { selector: 'select, .dropdown-input, input.real-input', constructor: function ($element) { return new Select($element) } },
    { selector: '.display-field', constructor: function ($element) { return new DisplayField($element) } },
    { selector: '.input-radio-fields, [type="radio"]', constructor: function ($element) { return new RadioGroup($element) } },
    { selector: '[type="checkbox"]', constructor: function ($element) { return new Checkbox($element) } },
    { selector: '[type="file"]', constructor: function ($element) { return new File($element) } },
    { selector: '.select-suggest-search > div:not(.suggest-search) > input', constructor: function ($element) { return new SelectSuggestSearch($element) } },
    { selector: '.phone-field, .phone-full-input', constructor: function ($element) { return new PhoneField($element) } },
    { selector: '.name-field, .name-full-input', constructor: function ($element) { return new NameField($element) } },
    { selector: '[data-file-field]', constructor: function ($element) { return new AsyncField($element) } },
    { selector: msdropdown.wireup, constructor: function ($element) { return new MultiSelectDropdown($element); } },
    { selector: '.datefield input', constructor: function ($element) { return new DateField($element); } },
    { selector: '[data-start="hidden"], [data-end="hidden"]', constructor: function ($element) { return new DateRangeDateField($element) } },
    { selector: '[data-daterange-input], [data-daterange-display]', constructor: function ($element) { return new DateRange($element[0]); } },
    { selector: '.money-field', constructor: function ($element) { return new MoneyField($element[0]); } },
    { selector: 'input, textarea', constructor: function ($element) { return new Input($element) } },
    { selector: duallistbox.wireup, constructor: function ($element) { return new DualListBoxElement($element); } },
  ];

  public constructor($element: JQuery) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');

    this._$element = $element.first();
    this.type = this.getType();
  }

  public static from(element: HTMLElement): Element {
    return new Element($(element));
  }

  public static addType(selector: string, constructor: (element: JQuery) => IElement): void {
    Element.map.unshift({ selector, constructor });
  }

  public static fromElements($elements: JQuery): Array<Element> {
    if (!$elements)
      throw new Error('$elements cannot be null');

    let result = new Array<Element>();
    $elements.each(function () {
      result.push(new Element($(this)));
    })

    return result;
  }

  private getType(): IElement {
    for (let i = 0; i < Element.map.length; i++) {
      if (this._$element.is(Element.map[i].selector))
        return Element.map[i].constructor(this._$element);
    }
    return new GenericElement(this._$element);
  }

  public value(value?: any): any | void {
    let currentValue = this.type.value();
    if (value === undefined)
      return currentValue;

    this.type.value(value);

    let newValue = this.type.value();
    if (currentValue != newValue)
      this.$valueElement.change();
  }

  public text(text?: string): string | void {
    return this.type.text(text);
  }

  public name(): string {
    return this.type.name();
  }

  public suppressValueChange(value: IAction | Object): Element {
    new SuppressesEvent().execute('change.rustig.element', this.$element, function () {
      if (typeof value === 'function') {
        (<IAction>value)();
      }
      else {
        this.value(value);
      }
    }.bind(this));

    return this;
  }
}

export interface IElement {
  value(value?: any): any | void;
  text(text?: any): any | void;
  name(): string;
  $userFacingElement: JQuery;
  $valueElement: JQuery;
}

class Select {
  private $element: JQuery;
  private _$userFacingElement: JQuery;
  private _$valueElement: JQuery;

  public get $userFacingElement(): JQuery { return this._$userFacingElement; }
  public get $valueElement(): JQuery { return this._$valueElement; }
  private get allowsCustomInput(): boolean { return this.$element.is('[data-allow-custom-input]') };

  public constructor($element: JQuery) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');

    let $root = $element.closest('.dropdown-field');
    this.$element = $root.find('select');
    this._$userFacingElement = $root.find('.dropdown-input');
    this._$valueElement = this.allowsCustomInput ? $root.find('.real-input') : this.$element;
  }

  public value(value?: any): any | void {
    if (value !== undefined) {
      new SuppressesEvent().execute('change', this.$element, function () {
        if (this.allowsCustomInput) {
          this.$valueElement.val(value);
          this.$userFacingElement.val(value);
        }
        else
          this.$element.val(value).trigger('dropdown:reload');
      }.bind(this));
      return;
    }
    return this.$valueElement.val();
  }

  public text(text?: string): string | number | string[] {
    if (text !== undefined) {
      if (this.allowsCustomInput) {
        this.value(text);
      }
      else {
        let $option = this.$element.find('option:contains(' + text + ')');
        this.value($option.val());
      }
      return;
    }
    return this.$userFacingElement.val();
  }

  public name(): string {
    return this.$element.attr('name');
  }
}

class Input {
  protected $element: JQuery;

  public get $userFacingElement(): JQuery { return this.$element; }
  public get $valueElement(): JQuery { return this.$element; }

  public constructor($element: JQuery) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');

    this.$element = $element;
  }

  public value(value?: any): any | void {
    if (value !== undefined) {
      this.$element.val(value);
      return;
    }
    return this.$element.val();
  }

  public text(text?: string): string | number | string[] {
    if (text !== undefined) {
      this.$element.val(text);
      return;
    }
    return this.$element.val();
  }

  public name(): string {
    return this.$element.attr('name');
  }
}

class DisplayField {
  private $element: JQuery;
  private $childElement: JQuery;

  public get $userFacingElement(): JQuery { return this.$element; }
  public get $valueElement(): JQuery { return this.$element; }

  public constructor($element: JQuery) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');

    this.$element = $element;
    this.$childElement = this.$element.children(':first');
  }

  public value(value?: any): any | void {
    return this.text(value);
  }

  public text(text?: string): string | void {
    let $text = this.$childElement.length > 0 ? this.$childElement : this.$element;

    if (text !== undefined)
      $text.text(text);
    else
      return $text.text();
  }

  public name(): string {
    return null;
  }
}

class RadioGroup {
  private $element: JQuery;

  public get $userFacingElement(): JQuery { return this.$element; }
  public get $valueElement(): JQuery { return this.$element; }

  public constructor($element: JQuery) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');

    this.$element = $element.closest('.input-radio-fields');
  }

  public value(value?: any): any | void {
    if (value !== undefined) {
      this.$element.find('input[type="radio"]:checked').removeAttr('checked');
      this.$element.find('input[type="radio"][value="' + value + '"]').prop('checked', true);
      return;
    }
    return this.$element.find('input[type="radio"]:checked').attr('value');
  }

  public text(text?: string): string | void {
    let $button = this.$element.find('input[type="radio"]:checked');
    let $label = $button.closest('label');

    if (text !== undefined) {
      $label.text(text);
      $label.prepend($button);
      return;
    }

    return $label.text();
  }

  public name(): string {
    return this.$element.find('[name]:first').attr('name');
  }
}

class Checkbox {
  private $element: JQuery;

  public get $userFacingElement(): JQuery { return this.$element; }
  public get $valueElement(): JQuery { return this.$element; }

  public constructor($element: JQuery) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');

    this.$element = $element;
  }

  public value(value?: any): any | void {
    let $checkbox = this.$element.is('input[type="checkbox"]') ? this.$element
      : this.$element.find('input[type="checkbox"]');

    if (value !== undefined) {
      $checkbox.prop('checked', value);
      return;
    }
    return $checkbox.is(':checked');
  }

  public text(text?: string): string | void {
    let $text = this.$element.closest('.input-checkbox').find('.input-checkbox-text');

    if (text !== undefined) {
      $text.text(text);
      return;
    }

    return $text.text();
  }

  public name(): string {
    return this.$element.attr('name');
  }
}

class File {
  private $element: JQuery;

  public get $userFacingElement(): JQuery { return this.$element; }
  public get $valueElement(): JQuery { return this.$element; }

  public constructor($element: JQuery) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');

    this.$element = $element;
  }

  public value(value?: any): any | void {
    if (value !== undefined) {
      this.resetFileInput();
      return;
    }
    return this.$element.val();
  }

  private resetFileInput(): void {
    try {
      this.$element.val('');
      if (this.$element.val()) {
        this.$element.attr('type', 'file');
      }
    } catch (e) { }
  }

  public text(text?: string): string | void {
    if (text !== undefined) {
      this.$element.text(text);
      return;
    }

    return this.$element.text();
  }

  public name(): string {
    return this.$element.siblings('input').attr('name');
  }
}

class GenericElement {
  private $element: JQuery;

  public get $userFacingElement(): JQuery { return this.$element; }
  public get $valueElement(): JQuery { return this.$element; }

  public constructor($element: JQuery) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');

    this.$element = $element;
  }

  public value(value?: any): any | void {
    if (value !== undefined) {
      this.$element.data('rustig.value', null);
      return;
    }

    return this.$element.data('rustig.value');
  }

  public text(text?: string): string | void {
    if (text !== undefined) {
      this.$element.text(text);
      return;
    }

    return this.$element.text();
  }

  public name(): string {
    return null;
  }
}

class SelectSuggestSearch {
  private $root: JQuery;
  private $valueInput: JQuery;
  private $textInput: JQuery;

  public get $userFacingElement(): JQuery { return this.$textInput; }
  public get $valueElement(): JQuery { return this.$valueInput; }

  public constructor($element: JQuery) {
    this.$root = $element.closest('.select-suggest-search');
    this.$valueInput = $element;
    this.$textInput = this.$root.find('.suggest-search input');
  }

  public value(value?: any): any | void {
    if (value !== undefined) {
      let $value = $('ss-key'.asDataSelector('Value') + 'value'.asAttrSelector(value));

      if ($value.length <= 0) {
        if (!value) {
          this.$valueInput.val(value);
          this.$textInput.val(value);
        }
        return;
      }

      let text = $value.closest('.ss-suggestion').find('ss-key'.asDataSelector('Text')).val();
      this.$textInput.val(text);
      return;
    }
    return this.$valueInput.val();
  }

  public text(text?: string): string | number | string[] {
    if (text !== undefined) {
      this.$textInput.val(text);
      return;
    }
    return this.$textInput.val();
  }

  public name(): string {
    return this.$valueInput.attr('name');
  }
}

class PhoneField {
  private $element: JQuery;
  private _$userFacingElement: JQuery;

  public get $userFacingElement(): JQuery { return this._$userFacingElement; }
  public get $valueElement(): JQuery { return this._$userFacingElement; }

  public constructor($element: JQuery) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');

    this.$element = $element.closest('.phone-field');
    this._$userFacingElement = this.$element.find('.phone-full-input');
  }

  public value(value?: any): any | void {
    if (value !== undefined) {
      this._$userFacingElement.val(value).change();
      return;
    }
    return this._$userFacingElement.val();
  }

  public text(text?: string): string | number | string[] {
    return this._$userFacingElement.val();
  }

  public name(): string {
    return this.$userFacingElement.attr('name');
  }
}

class NameField {
  private $element: JQuery;
  private _$userFacingElement: JQuery;

  public get $userFacingElement(): JQuery { return this._$userFacingElement; }
  public get $valueElement(): JQuery { return this._$userFacingElement; }

  public constructor($element: JQuery) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');

    this.$element = $element.closest('.name-field');
    this._$userFacingElement = this.$element.find('.name-full-input');
  }

  public value(value?: any): any | void {
    if (value !== undefined) {
      this._$userFacingElement.val(value).change();
      return;
    }
    return this._$userFacingElement.val();
  }

  public text(text?: string): string | number | string[] {
    return this._$userFacingElement.val();
  }

  public name(): string {
    return this.$userFacingElement.attr('name');
  }
}

class AsyncField {
  private $element: JQuery;
  private field: () => AsyncFileField;

  public get $userFacingElement(): JQuery { return this.$element; }
  public get $valueElement(): JQuery { return this.field().$valueElement; }

  public constructor($element: JQuery) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');

    this.$element = $element;
    this.field = () => <AsyncFileField>this.$element.data('rustig.asyncfilefield')
  }

  public value(value?: any): any | void {
    return this.field().value(value);
  }

  public text(text?: any): string | void {
    return this.field().text(text);
  }

  public name(): string {
    return this.field().name();
  }
}

class MultiSelectDropdown {
  private $element: JQuery;
  private field: () => msdropdown;

  public get $userFacingElement(): JQuery { return this.field().$userFacingElement; }
  public get $valueElement(): JQuery { return this.field().$valueElement; }

  public constructor($element: JQuery) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');

    this.$element = $element;
    this.field = () => <msdropdown>this.$element.data('rustig.multiselectdropdown')
  }

  public value(value?: any): any | void {
    return this.field().value(value);
  }

  public text(text?: any): string | void {
    return this.field().text(text);
  }

  public name(): string {
    return this.field().name();
  }
}

class DateField extends Input {

  public constructor($element: JQuery) {
    super($element);
  }

  public value(value?: any): any | void {
    if (value !== undefined) {
      let datePicker: any = this.$element.datefield().data('DateTimePicker');

      if (datePicker)
        datePicker.date(value);
      else
        this.$element.val(value);

      return;
    }
    return new Date(this.$element.val() as string).isValid() ? this.$element.val() : '';
  }
}

class DateRangeDateField extends Input {

  public get $userFacingElement(): JQuery {
    let element = <HTMLElement>this.$element[0];
    let root = element.closest('[data-daterange]');
    let input = root.querySelector('[data-daterange-input]');
    let key = input.getAttribute('data-daterange-key');
    let popoverContent = document.querySelector('daterange-popover-content'.asDataSelector(key));
    let selector = element.hasAttribute('data-start') ? '[data-start]' : '[data-end]';
    let userFacingElement = <HTMLElement> popoverContent.querySelector(selector);
   
    return $(userFacingElement);
  }

  public constructor($element: JQuery) {
    super($element);
  }
}

class DateRange {
  private dateRangeField: DateRangeField;

  protected $element(): JQuery { return $(this.dateRangeField.element); }
  public get $userFacingElement(): JQuery { return $(this.dateRangeField.userFacingElement); }
  public get $valueElement(): JQuery { return $(this.dateRangeField.valueElement); }

  public constructor(element: HTMLElement) {
    if (!element)
      throw new Error('element cannot be null');

    let root = element.closest('[data-daterange]');
    let input = <IDateRangeElement>root.querySelector('[data-daterange-input]');
    this.dateRangeField = DateRangeField.load(input);
  }

  public value(value?: any): any | void {
    return this.dateRangeField.value(value);
  }

  public text(text?: string): string | void {
    return this.dateRangeField.text(text);
  }

  public name(): string {
    return this.dateRangeField.name();
  }
}

class MoneyField {
  protected element: HTMLInputElement;

  public get $userFacingElement(): JQuery { return $(this.element); }
  public get $valueElement(): JQuery { return $(this.element); }

  public constructor(element: HTMLInputElement) {
    if (!element)
      throw new Error('element cannot be null');

    this.element = element;
  }

  public value(value?: any): any | void {
    if (value !== undefined) {
      if (!value && value !== 0)
        value = '';
      $(this.element).autoNumeric('set', value);
      return;
    }
    return $(this.element).autoNumeric('get', value);
  }

  public text(text?: string): string | number | string[] {
    if (text !== undefined) {
      $(this.element).autoNumeric('set', text);
      return;
    }
    return this.element.value;
  }

  public name(): string {
    return this.element.getAttribute('name');
  }
}

class DualListBoxElement {
  private $element: JQuery;

  public get $userFacingElement(): JQuery { return this.$element; }
  public get $valueElement(): JQuery { return this.$element.find('[data-selected-values-csv]'); }

  public constructor($element: JQuery) {
    if (!$element)
      throw new Error('element cannot be null');
    if ($element.length > 1)
      throw new Error('element must be a single element');
    this.$element = $element;
  }

  public value(value?: any): any | void {
    if (value !== undefined) {
      console.log('Setting value on DualListBoxElement not implemented');
      return;
    }
    return this.$valueElement.val();
  }

  public text(text?: string): string | number | string[] {
    console.log('Get\Set text for DualListBoxElement not implemented');
    return null;
  }

  public name(): string {
    return this.$element.find('[data-selected-results] input[value=""]').attr('name');
  }
}

export interface IAction {
  (): void;
}
export default Element;
